import { useEffect, useRef, useState } from "react";
import DayClosingHeaderComponent from "../DayClosingHeaderComponent";
import AppointmentClosingComponent from "../AppointmentClosingComponent";
import { useLocation, useNavigate } from "react-router";
import JobCardClosingComponent from "../JobCardClosingComponent";
import LabReportClosureComponent from "../LabReportClosureComponent";
import CashClosureComponent from "../CashClosureComponent";
import GRNClosureComponent from "../GRNClosureComponent";
import PaymentsClosingComponent from "../PaymentsClosingComponent";
import ReturnsClosureComponent from "../ReturnsClosureComponent";
import DeliveryClosureComponent from "../DeliveryClosureComponent";
import ReceivingClosureComponent from "../ReceivingClosureComponent";
import BadInventoryClosureComponent from "../BadInventoryClosureComponent";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import DayclosingSummaryComponent from "../DayclosingSummaryComponent";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useDayClosingCurrentShift } from "@/app/hooks/api_hooks/dayClosing/useDayClosingCurrentShift";
import ServiceInvoicesClosingComponent from "../ServiceInvoicesClosingComponent";
import VideoConsultationClosingComponent from "../VideoConsultationClosingComponent";
import OpenCartsClosingComponent from "../OpenCartsClosingComponent";
import StaffAttendanceClosingComponent from "../StaffAttendanceClosingComponent";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";

export default function DayClosingComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { DAY_CLOSURE_TAB } = useArrayConstants();

  const [activeChild, setActiveChild] = useState<string | null>(DAY_CLOSURE_TAB[0]?.id ?? null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const { mappedCityId, mappedClinicId } = useAppSelector((state) => state.root.UIStates);

  const { execute: fetchCurrentShift, closureData: shift } = useDayClosingCurrentShift();

  useEffect(() => {
    if (mappedClinicId) {
      fetchCurrentShift(mappedClinicId);
    }
  }, [mappedClinicId]);

  // keep activeChild in sync with query param
  const params = new URLSearchParams(location.search);
  const processId = params.get("closing_process");
  const isClinicView = params.get("is_clinic") === "CLINIC";

  useEffect(() => {
    if (processId && DAY_CLOSURE_TAB?.some((p) => p.id === processId)) {
      setActiveChild(processId);
    }
  }, [location.search, DAY_CLOSURE_TAB]);

  // set active first then navigate (so scroll wrapper has a chance to run)

  // RBAC: ACCOUNTING_CASH_CLOSING_CENTRAL_VIEW — legacy semantics: everyone EXCEPT
  // clinic_ops / clinic_ops_bluecity / clinic_ops_doggyworld.
  const isCentralOpps = useFeatureAccess("ACCOUNTING_CASH_CLOSING_CENTRAL_VIEW");

  const handleProcessClick = (processId: string) => {
    setActiveChild(processId);
    isClinicView
      ? navigate(
        `/app/closing/day-closing?closing_process=${processId}&city_id=${mappedCityId}&clinic_ids=${mappedClinicId}&is_clinic=CLINIC&shift=${shift?.shift}&t=${Date.now()}`,
      )
      : navigate(
        `/app/closing/day-closing?closing_process=${processId}&city_id=${mappedCityId}&clinic_ids=${mappedClinicId}&shift=${shift?.shift}&t=${Date.now()}`,
      );
  };

  return (
    <div className="p-2 sm:p-3 bg-gray-50">
      <DayClosingHeaderComponent />
      {isCentralOpps && !isClinicView ? (
        <DayclosingSummaryComponent />
      ) : (
        <>
          <div className="bg-white rounded-lg p-2">
            {/* tab strip */}
            <div
              ref={containerRef}
              className="flex gap-2 w-full overflow-x-auto lg:overflow-x-visible scrollbar-hide items-center whitespace-nowrap lg:whitespace-normal pb-0 lg:pb-0 lg:flex-wrap"
            >
              {DAY_CLOSURE_TAB?.map((process) => (
                <button
                  key={process.id}
                  type="button"
                  ref={(el) => (tabRefs.current[process.id] = el)}
                  onClick={() => handleProcessClick(process.id)}
                  className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm transition-colors whitespace-nowrap rounded-full shrink-0
                  ${activeChild === process.id
                      ? "bg-black text-white shadow-md font-semibold"
                      : "bg-[#F3F3F3] text-[#595F6C] font-medium"
                    }`}
                >
                  {process.name}
                </button>
              ))}
            </div>
          </div>

          <div className="h-auto mt-2">
            {activeChild === "p0" && <DayclosingSummaryComponent />}
            {activeChild === "p1" && <AppointmentClosingComponent />}
            {activeChild === "p2" && <PaymentsClosingComponent />}
            {activeChild === "p3" && <CashClosureComponent />}
            {activeChild === "p4" && <JobCardClosingComponent />}
            {activeChild === "p5" && <LabReportClosureComponent />}
            {activeChild === "p6" && <LabReportClosureComponent reportType="external" />}
            {activeChild === "p7" && <ReceivingClosureComponent />}
            {activeChild === "p8" && <GRNClosureComponent />}
            {activeChild === "p9" && <DeliveryClosureComponent />}
            {activeChild === "p10" && <ReturnsClosureComponent />}
            {activeChild === "p11" && <BadInventoryClosureComponent />}
            {activeChild === "p12" && <ServiceInvoicesClosingComponent />}
            {activeChild === "p13" && <VideoConsultationClosingComponent />}
            {activeChild === "p14" && <OpenCartsClosingComponent />}
            {activeChild === "p15" && <StaffAttendanceClosingComponent />}
          </div>
        </>
      )}
    </div>
  );
}
